import { computed, inject, Injectable, signal } from "@angular/core";
import { Observable, switchMap, throwError, from, tap, map, catchError, } from "rxjs";
import { User, LoginRequest, LoginResponse, Modules, UserValues, UserUtility, Profile,ApiResponse } from "../types/apiresponse.interface";
import { ApiService, MODULE_KEY_MAP, ADDON_KEY_MAP } from "./api.service";
import { DeviceService } from "./device.service";
import { EncryptionService, session_storage_keys } from "./encryption.service";
import { isPlatformBrowser } from "@angular/common";
import { AgentAuthService } from "./agent-auth.service";
const TOKEN_KEY = 'litops_auth_token';
const AUTO_RENEW_KEY = 'litops_auto_renew';
@Injectable({
    providedIn: 'root',
  })
  export class AuthService {
    readonly currentUser = signal<User | null>(null);
    readonly isAuthenticated = computed(() => !!this.currentUser());
    readonly isExpiryModalOpen = signal(false);
    readonly autoRenewSession = signal<boolean>(false);
    
    private expirationTimer: any;
    constructor(private apiService: ApiService,private deviceService: DeviceService,
        private encryptionService: EncryptionService,private agentAuthService: AgentAuthService 
      ) {}

    login(email: string, pass: string): Observable<User> {
        return this.deviceService.buildDeviceModel().pipe(
          switchMap(device => {
      
            const payload: LoginRequest = {
              Username: email,
              Password: pass,
              LoginDateTime: new Date().toISOString(),
              ipAddress: device.ipAddress,
              deviceName: device.deviceName,
              countryName: device.countryName,
              currentLocation: device.currentLocation,
              osDetails: device.osDetails?.substring(0, 100),
              deviceTime: device.deviceTime,
              
            };
      
            return this.apiService.postPublicAgent<LoginResponse>(
              'Agent/Login',
              payload
            );
          }),
      
          switchMap((apiResponse:any) => {
      
            const result = apiResponse?.result;
            this.agentAuthService.storeResponse(apiResponse, {Username:email,Password:pass});
            if (!result?.userValues) {
              return throwError(() => new Error('Invalid login response'));
            }
      
            const userTypeInfo = this.getUserType(result.userValues);
      
            const userData: User = {
              name: result.userValues.userName,
              email: result.userValues.emailID,
              password: result.userValues.password,
              initials: result.userValues.userType,
              title: "",
              location: "",
              earnings: 0,
              projects: 0,
              successRate: 0,
              profileCompletion: 0,
              environment: userTypeInfo.userEnvironment,
              token: result.token,
              passkey: result.passkey,
              twoFactorKey: result.twoFactorKey,
              masterRoleId: result.masterRoleId,
              defaultCompanyId: result.defaultCompanyId,
              authenticatorAppStatus: result.authenticatorAppStatus,
              subscriptionStatus: result.subscriptionStatus,
              requestUrl: result.requestUrl,
              userValues: result.userValues,
              userType: result.userValues.userType,
              masterId: userTypeInfo.userMasterId
            };
      
            /* ================= MEMORY STORE ================= */
            this.encryptionService.setAuthValue('token', userData.token);
            this.encryptionService.setAuthValue('username', userData.email);
            this.encryptionService.setAuthValue('passkey', userData.passkey);
            //added By SG for getting email and password for prime login for module upgrade purpose////// 
            this.encryptionService.setAuthValue('password', userData.password);
            ///////////////////////////////////////////////////
            this.encryptionService.setAuthValue('masterId', String(userData.masterId));
      
            return from(Promise.all([
              this.encryptionService.encryptAsync(userData.requestUrl, session_storage_keys.DEFAULT_URL),
              this.encryptionService.encryptAsync(userData.token, session_storage_keys.DEFAULT_TOKEN),
              this.encryptionService.encryptAsync(userData.email, session_storage_keys.DEFAULT_USER_NAME),
              this.encryptionService.encryptAsync(userData.password, session_storage_keys.DEFAULT_PASSWORD),
              this.encryptionService.encryptAsync(userData.masterId, session_storage_keys.DEFAULT_MASTER_ID),
              this.encryptionService.encryptAsync(userData.name, session_storage_keys.USER_NAME),
              this.encryptionService.encryptAsync(userData.passkey, session_storage_keys.DEFAULT_PASS_KEY),
              this.encryptionService.encryptAsync(userData.masterRoleId, session_storage_keys.DEFAULT_MASTER_ROLE_ID),
              this.encryptionService.encryptAsync(userData.defaultCompanyId, session_storage_keys.DEFAULT_COMPANY_ID),
              this.encryptionService.encryptAsync(userData.environment, session_storage_keys.CURRENT_USER_ENVIRONMENT),
            ])).pipe(
              switchMap(() =>
                this.getProfile(userTypeInfo.userProfileEndpoint)
              ),
              tap(profile => {
                const subscriberModules = profile?.subscriberModules ?? [];
                const standardReportFeatures = profile?.standardReportFeatures ?? [];
                const companyInfo = profile?.companyInfo ?? [];
                const mergedModules: Modules[] = [];
                const userProfile= profile?.userProfile ?? [];
                ////////////////added by SG for company switch //////////////
                this.encryptionService.encryptAsync(
                  companyInfo,
                  session_storage_keys.COMPANY_LIST
                );
                this.encryptionService.encryptAsync(
                  standardReportFeatures,
                  session_storage_keys.STANDARD_REPORT
                );
                this.encryptionService.encryptAsync(
                  subscriberModules,
                  session_storage_keys.SUBSCRIBE_LIST
                );
                this.encryptionService.encryptAsync(
                  userProfile,
                  session_storage_keys.USER_PROFILE
                );
                
                ////////////////end///////////////
                // this.encryptionService.encryptAsync(subscriberModules[0].subscriptionID, session_storage_keys.DEFAULT_SUBSCRIPTION_ID),
                this.encryptionService.encryptAsync(subscriberModules[0]?.subscriptionID ? subscriberModules[0]?.subscriptionID : companyInfo[0]?.adminSubscribers[0]?.subscriptionID, session_storage_keys.DEFAULT_SUBSCRIPTION_ID),
                  subscriberModules?.forEach(module => {
                    
                    /* ===================== MAIN MODULE ===================== */
                    const moduleStorageKey = MODULE_KEY_MAP[module.moduleID];
      
                    if (moduleStorageKey) {
      
                      const cleanModule: Modules = {
                        managementId: module.moduleManagementID,
                        id: module.moduleID,
                        key: module.moduleKey,
                        sourceControl: module.moduleSourceControl,
                        accessToken: module.moduleAccessToken,
                        features: module.moduleManagementFeatures?.
                          filter(feature => feature.moduleManagementID === module.moduleManagementID)
                          .map(feature => ({
                            featureId: feature.featureID,
                            featureKey: feature.featureKey,
                            managementId: feature.moduleManagementID
                          })) ?? []
                      };
      
                      this.encryptionService.encryptAsync(
                        cleanModule,
                        moduleStorageKey
                      );
      
                      mergedModules.push(cleanModule);
                    }
      
                    /* ===================== ADD-ON MODULES ===================== */
                    (module.moduleManagementAddOnModules ?? []).forEach(addOn => {
      
                      const addOnStorageKey = ADDON_KEY_MAP[addOn.addOnModuleID];
      
                      if (addOnStorageKey) {
      
                        const cleanAddOn: Modules = {
                          managementId: addOn.moduleManagementAddOnModuleID,
                          id: addOn.addOnModuleID,
                          key: addOn.addOnModuleKey,
                          accessToken: addOn.addOnModuleAccessToken,
                          sourceControl: addOn.addOnModuleSourceControl,
                          features: addOn.moduleManagementAddOnModuleFeature?.
                            filter(feature => feature.moduleManagementAddOnModuleID === addOn.moduleManagementAddOnModuleID)
                            .map(feature => ({
                              featureId: feature.featureID,
                              featureKey: feature.featureKey,
                              managementId: feature.moduleManagementAddOnModuleID
                            })) ?? []
                        };
      
                        this.encryptionService.encryptAsync(
                          cleanAddOn,
                          addOnStorageKey
                        );
      
                        mergedModules.push(cleanAddOn);
                      }
                    });
      
                  });
      
      
                /* ===================== SINGLE STORAGE ===================== */
                this.encryptionService.encryptAsync(
                  mergedModules,
                  session_storage_keys.MERGED_MODULES_LIST
                );
      
      
              })
              ,
              map(() => userData)
            );
          }),
      
          tap(userData => {
            
            const token = this._generateMockToken(userData, 2);
            sessionStorage.setItem(TOKEN_KEY, token);
            this._processToken(token);
          }),
      
          catchError(error => {
            console.error('Login or profile fetch failed', error);
            return throwError(() => error);
          })
        );
      }
      private getUserType(userValues?: UserValues, userType?: string): UserUtility {
        let info: UserUtility = {} as UserUtility;
      
        const effectiveUserType = userType ?? userValues?.userType;
        if (!effectiveUserType) {
          return info;
        }
      
        if (effectiveUserType === 'PADM') {
          info.userTypeShort = 'PADM';
          info.userProfileEndpoint = 'Prime';
          info.userEnvironment = 'prime';
      
          if (userValues != null) {
            info.userMasterId = userValues.primeAdminId;
          }
        }
        else if (effectiveUserType === 'ADM') {
          info.userTypeShort = 'ADM';
          info.userProfileEndpoint = 'AdminSubscribe';
          info.userEnvironment = 'subscriber';
      
          if (userValues != null) {
            info.userMasterId = userValues.userId;
          }
        }
        else if (effectiveUserType === 'DEV') {
          info.userTypeShort = 'DEV';
          info.userProfileEndpoint = 'Developer';
          info.userEnvironment = 'developer';
      
          if (userValues != null) {
            info.userMasterId = userValues.devID;
          }
        }
      
        return info;
      }
      
      private getProfile(userType: string): Observable<Profile> {
      
        return from(
          Promise.all([
            this.encryptionService.decryptAsync<any>(session_storage_keys.DEFAULT_MASTER_ID),
            this.encryptionService.decryptAsync<any>(session_storage_keys.DEFAULT_PASS_KEY)
          ])
        ).pipe(
          switchMap(([masterId, passkey]) => {
      
            if (!masterId || !passkey) {
              throw new Error('Missing MasterID or passkey from session storage');
            }
      
            const payload = {
              MasterID: masterId,
              passkey,
              MenuGroup: { isBoth: true, isEnableMobileView: true, displayMenu: true },
              MainMenu: { isBoth: true, isEnableMobileView: true, displayMenu: true },
              SubMenu: { isBoth: true, isEnableMobileView: true, displayMenu: true },
              Component: { isBoth: true, isEnableMobileView: true, displayMenu: true }
            };
      
            return this.apiService.postAuthorizedDefaultModule<ApiResponse>(
              `api/GetProfile/${userType}`,
              payload
            );
          }),
      
          map(response => {
            if (!response?.result) {
              throw new Error('No profile data found in the response');
            }
            return response.result as Profile;
          }),
      
          catchError(error => {
            console.error('Error fetching profile:', error);
            return throwError(() => error);
          })
        );
      }

      private _generateMockToken(user: User, expiresInMinutes: number): any {
        
        const header = { alg: 'HS256', typ: 'JWT' };
        const now = Math.floor(Date.now() / 1000);
        const payload = {
          ...user,
          iat: now,
          exp: now + (expiresInMinutes * 60)
        };
    
        const base64UrlEncode = (data: object) =>
          btoa(JSON.stringify(data));
    
        return `${base64UrlEncode(header)}.${base64UrlEncode(payload)}.dummySignature`;
      }

      private _processToken(token: string, payload: any = null): void {
        
        const tokenPayload = payload || this._parseJwt(token);
        if (!tokenPayload) {
          this.logout();
          return;
        }
    
        this.currentUser.set({
          name: tokenPayload.name,
          email: tokenPayload.email,
          password: tokenPayload.password,
          initials: tokenPayload.initials,
          title: tokenPayload.title,
          location: tokenPayload.location,
          earnings: tokenPayload.earnings,
          projects: tokenPayload.projects,
          successRate: tokenPayload.successRate,
          profileCompletion: tokenPayload.profileCompletion ?? 50,
          environment: tokenPayload.environment,
          token: tokenPayload.token,
          passkey: tokenPayload.passkey,
          twoFactorKey: tokenPayload.twoFactorKey,
          masterRoleId: tokenPayload.masterRoleId,
          defaultCompanyId: tokenPayload.defaultCompanyId,
          authenticatorAppStatus: tokenPayload.authenticatorAppStatus,
          subscriptionStatus: tokenPayload.subscriptionStatus,
          requestUrl: tokenPayload.requestUrl,
          userValues: tokenPayload.userValues,
          userType: tokenPayload.userType,
          masterId: tokenPayload.masterId
        });
    
        this._scheduleTokenExpirationCheck(tokenPayload.exp * 1000);
      }
      private _scheduleTokenExpirationCheck(expiryTime: number): void {
        
        const now = Date.now();
        const timeUntilExpiry = expiryTime - now;
        const checkLeadTime = 30 * 1000; // 30 seconds before expiry
        const checkTimeout = timeUntilExpiry - checkLeadTime;
    
        if (checkTimeout > 0) {
          this.expirationTimer = setTimeout(() => {
            this._handleTokenExpiration();
          }, checkTimeout);
        } else {
          // If we are already within the lead time or expired, trigger immediately
          this._handleTokenExpiration();
        }
      }

      private _handleTokenExpiration(): void {
        console.log('Token is about to expire. Auto-renew:', this.autoRenewSession());
        if (this.autoRenewSession()) {
          this.refreshToken(2); // Default extension for auto-renew
        } else {
          // Open modal if not already open
          if (!this.isExpiryModalOpen()) {
            this.isExpiryModalOpen.set(true);
           
          }
        }
      }

      refreshToken(hours: number): void {
      this.isExpiryModalOpen.set(false);
    
        const tokenString =sessionStorage.getItem(TOKEN_KEY) ?? null;
        const oldPayload = this._parseJwt(tokenString || '');
    
        if (oldPayload) {
          const userPayload: User = {
            name: oldPayload.name,
            email: oldPayload.email,
            password: oldPayload.password,
            initials: oldPayload.initials,
            title: oldPayload.title,
            location: oldPayload.location,
            earnings: oldPayload.earnings,
            projects: oldPayload.projects,
            successRate: oldPayload.successRate,
            profileCompletion: oldPayload.profileCompletion,
            environment: oldPayload.environment,
            token: oldPayload.token,
            passkey: oldPayload.passkey,
            twoFactorKey: oldPayload.twoFactorKey,
            masterRoleId: oldPayload.masterRoleId,
            defaultCompanyId: oldPayload.defaultCompanyId,
            authenticatorAppStatus: oldPayload.authenticatorAppStatus,
            subscriptionStatus: oldPayload.subscriptionStatus,
            requestUrl: oldPayload.requestUrl,
            userValues: oldPayload.userValues,
            userType: oldPayload.userType,
            masterId: oldPayload.masterId
          };
    
          const expiresInMinutes = hours * 60;
    
          const token = this._generateMockToken(userPayload, expiresInMinutes);
          sessionStorage.setItem(TOKEN_KEY, token);
          this._processToken(token);
          console.log(`Session renewed for ${hours} hour(s).`);
        } else {
          this.logout();
        }
      }

      private _parseJwt(token: string): any | null {
       try {
        
          const parts = token.split('.');
          if (parts.length !== 3) return null;
    
          const base64Url = parts[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(atob(base64).split('').map(c =>
            '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
          ).join(''));
          return JSON.parse(jsonPayload);
        } catch (error) {
          console.error("Error parsing JWT:", error);
          return null;
        }
      }
      logout(): void {
        this.currentUser.set(null);
    this.encryptionService.clearAll();
        this.encryptionService.clearAuthMemory();
       // this.router.navigate(['/login']);
      }
    
  }